<template>
  <div
    class="text-strongest flex w-full"
    :class="props.vertical ? 'flex-col lg:flex-row gap-2 md:gap-5' : 'flex-col'"
  >
    <!-- ✅ HORIZONTAL MODE: SubNavigation scrolls on mobile -->
    <div v-if="!props.vertical" class="w-full overflow-x-hidden">
      <!-- scroll container -->
      <div
        class="w-full overflow-x-auto overscroll-x-contain"
        style="-webkit-overflow-scrolling: touch"
      >
        <!-- make content allowed to be wider than viewport -->
        <div class="min-w-max">
          <SubNavigation
            ref="subnav"
            :tabs="normalizedTabs"
            :active-tab="effectiveActiveTab"
            @update:activeTab="onSelectTab"
            @tab-action="(payload) => emit('tab-action', payload)"
            :use-active-icon="props.useActiveIcon"
            :active-icon="props.activeIcon"
            :vertical="false"
            class="pb-6"
          />
        </div>
      </div>
    </div>

    <!-- ✅ VERTICAL MODE: Desktop Subnav -->
    <div v-else class="hidden lg:flex">
      <div class="self-start sticky top-24 max-h-[calc(100vh-3rem)] overflow-y-auto">
        <SubNavigation
          ref="subnav"
          :tabs="normalizedTabs"
          :active-tab="effectiveActiveTab"
          @update:activeTab="onSelectTab"
          @tab-action="(payload) => emit('tab-action', payload)"
          :use-active-icon="props.useActiveIcon"
          :active-icon="props.activeIcon"
          :vertical="true"
        />
      </div>
    </div>

    <!-- ✅ VERTICAL MODE: Mobile NavSheet -->
    <div v-if="props.vertical" class="flex lg:hidden w-full">
      <NavSheet
        ref="navsheet"
        :tabs="normalizedTabs"
        :active-tab="effectiveActiveTab"
        @update:activeTab="(key) => closeNavSheet(key)"
        @tab-action="(payload) => emit('tab-action', payload)"
        :use-active-icon="props.useActiveIcon"
        :active-icon="props.activeIcon"
        class="lg:mb-6"
      />
    </div>

    <!-- Content -->
    <div class="w-full h-auto min-w-0">
      <template v-for="tab in normalizedTabs" :key="tab.key">
        <div v-if="tab.subtabs && effectiveActiveTab.startsWith(tab.key)" class="h-full">
          <div
            v-for="sub in tab.subtabs"
            :key="sub.key"
            v-show="sub.key === effectiveActiveTab"
            class="h-full"
          >
            <slot :name="sub.slot_name || sub.key" :key="sub.slot_name">
              <div
                class="h-full w-full bg-container-bg rounded-xl flex items-center justify-center px-3 py-5 md:px-6 md:py-6"
              >
                <p class="text-lighter">This is fallback content for {{ sub.label }}.</p>
              </div>
            </slot>
          </div>
        </div>

        <div v-else v-show="tab.key === effectiveActiveTab" class="h-full">
          <slot :name="tab.slot_name || tab.key" :key="tab.slot_name">
            <div
              class="h-full w-full bg-container-bg rounded-xl flex items-center justify-center px-3 py-5 md:px-6 md:py-6"
            >
              <p class="text-lighter">This is fallback content for {{ tab.label }}.</p>
            </div>
          </slot>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { SubNavigation, NavSheet } from "@/components";
import { ref, computed, onMounted, watch } from "vue";

// props
const props = defineProps({
  tabs: {
    type: Array,
    default: () => [
      { label: "Tab 1", subtabs: [{ label: "Sub 1" }, { label: "Sub 2" }] },
      { label: "Tab 2" },
      { label: "Tab 3" },
    ],
  },
  vertical: { type: Boolean, default: false },
  useActiveIcon: { type: Boolean, default: false },
  activeIcon: { type: String, default: "Sticker" },

  // ✅ controlled active tab (optional)
  activeTab: { type: String, default: "" },
  jumpToRollovers: { type: Boolean, default: false }, // if true, will jump to "reinvest" tab on init instead of first tab
  initialTab: { type: String, default: "" }, // uncontrolled: tab key to open on mount (falls back to first tab)
});

const emit = defineEmits(["update:activeTab", "tab-action"]);

defineExpose({
  onSelectTab,
});

const subnav = ref(null);
const navsheet = ref(null);

const internalActiveTab = ref("");

// effective active tab: controlled if props.activeTab is set
const effectiveActiveTab = computed(() =>
  props.activeTab ? props.activeTab : internalActiveTab.value
);

const normalizedTabs = computed(() => {
  return props.tabs.map((tab) => {
    const tabKey = tab.key || slugify(tab.label);

    const normalizedSubtabs = tab.subtabs?.map((sub) => {
      // stable key if provided, fallback to old behavior
      const subKey = sub.key || `${tabKey}.${slugify(sub.label)}`;

      return {
        ...sub,
        label: sub.label,
        key: subKey,
        icon: sub.icon || null,

        // ✅ stable slot_name if provided; else fallback to key (old behavior)
        slot_name: sub.slot_name || sub.slot || subKey,

        // pass through action if present
        action: sub.action || null,

        // notification badge number if present
        notif_badge_number: sub.notif_badge_number || null,
      };
    });

    return {
      ...tab,
      label: tab.label,
      key: tabKey,
      icon: tab.icon || null,
      slot_name: tab.slot_name || tab.slot || tabKey,
      subtabs: normalizedSubtabs,
    };
  });
});

// initialize active tab if uncontrolled
onMounted(() => {
  if (!props.activeTab) {
    const first = normalizedTabs.value?.[0];
    if (!first) return;

    // An explicit `initialTab` (matching a real tab key) wins, then the legacy
    // `jumpToRollovers` shortcut, else the first tab.
    const requestedTab =
      props.initialTab && normalizedTabs.value.some((t) => t.key === props.initialTab)
        ? props.initialTab
        : null;

    if (first.subtabs?.length) {
      internalActiveTab.value = requestedTab || first.subtabs[0].key;
      if (props.vertical && subnav.value) {
        subnav.value.openTab = first.key;
      }
    } else {
      internalActiveTab.value = requestedTab || (props.jumpToRollovers ? "reinvest" : first.key);
    }
  }

  // if controlled and starts on a subtab, open parent menu
  openParentMenuForActive();
});

// if effective tab changes (either internal change or controlled change), open parent menu on desktop
watch(
  () => effectiveActiveTab.value,
  () => {
    openParentMenuForActive();
  }
);

function openParentMenuForActive() {
  if (!props.vertical || !subnav.value) return;

  const active = effectiveActiveTab.value;
  const parent = normalizedTabs.value.find((t) => t.subtabs?.some((s) => s.key === active));
  if (parent) {
    subnav.value.openTab = parent.key;
  }
}

function onSelectTab(key) {
  // if controlled, just emit
  if (props.activeTab) {
    emit("update:activeTab", key);
    return;
  }

  // uncontrolled
  internalActiveTab.value = key;
  emit("update:activeTab", key);
}

function closeNavSheet(key) {
  onSelectTab(key);
  if (navsheet.value) navsheet.value.isOpen = false;
}

function slugify(str = "") {
  return str
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-");
}
</script>
